/**
 * Service: sign-in email change (OTP-verified).
 *
 * Three server calls, in order:
 *   1. request-email-change — validates + has GoTrue send the code to the NEW
 *      address, and mirrors the token as token_type 'email_change'.
 *   2. verifyOtp({ type: 'email_change' }) — verifies against the NEW address.
 *      This is the whole point: the code proves control of the new inbox.
 *   3. notify-email-changed — security notice to the OLD address. A failed send
 *      pages an operator; it is not allowed to fail silently.
 */
import { supabase } from "@/integrations/supabase/client";

export interface EmailChangeResult {
  ok: boolean;
  error?: string;
}

async function readFunctionError(error: unknown): Promise<string> {
  // supabase.functions.invoke flattens everything to "non-2xx"; read the body.
  const anyErr = error as { context?: { text?: () => Promise<string> }; message?: string };
  if (anyErr?.context?.text) {
    try {
      const raw = await anyErr.context.text();
      const parsed = JSON.parse(raw) as { error?: string; detail?: string; reason?: string };
      return parsed.error ?? parsed.reason ?? parsed.detail ?? raw;
    } catch {
      /* fall through */
    }
  }
  return anyErr?.message ?? "unexpected";
}

/** Step 1 — send the verification code to the NEW address. */
export async function requestEmailChange(newEmail: string): Promise<EmailChangeResult> {
  const { data, error } = await supabase.functions.invoke("request-email-change", {
    body: { new_email: newEmail },
  });
  if (error) return { ok: false, error: await readFunctionError(error) };
  if (!data?.ok) return { ok: false, error: data?.error ?? "request_failed" };
  return { ok: true };
}

/**
 * Step 2 + 3 — verify the code against the NEW address, then notify the OLD one.
 *
 * The notify call is awaited and its failure is surfaced to the user: the change
 * has already happened at this point, so we tell them the notice didn't go out
 * rather than pretending everything is fine. The server has already alerted.
 */
export async function confirmEmailChange(
  newEmail: string,
  code: string,
  oldEmail: string,
): Promise<EmailChangeResult & { noticeSent?: boolean }> {
  const { error: verifyErr } = await supabase.auth.verifyOtp({
    email: newEmail,
    token: code.trim(),
    type: "email_change",
  });
  if (verifyErr) return { ok: false, error: verifyErr.message };

  const { data, error } = await supabase.functions.invoke("notify-email-changed", {
    body: { old_email: oldEmail },
  });
  if (error || !data?.ok) {
    return { ok: true, noticeSent: false, error: error ? await readFunctionError(error) : data?.error };
  }
  return { ok: true, noticeSent: true };
}
